import { Fragment, useEffect, useMemo, useState } from 'react';
import { AlertTriangle, Layers, Loader2, RotateCcw, Save, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Sheet,
  SheetBody,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { RemoteSearchSelect } from '@/components/ui/remote-search-select';
import { migrationV1V2Api } from '../api/migration-v1-v2';
import type {
  EnCodeOption,
  ReferentialOverrideRow,
  ReferentialOverridesResponse,
  V2DataItem,
} from '../api/migration-v1-v2';
import { LOT_MAPPING_REGISTRY } from './lotMappingRegistry';
import { MatiereNiveauBcnSection } from './MatiereNiveauBcnSection';

const NONE_VALUE = '__none__';

/** Formate une option EN (MEF/matière) pour l'affichage, badge langue vivante inclus. */
function enOptionLabel(o: EnCodeOption): string {
  if (o.isLangueVivante) {
    const rang = o.rang ? `LV${o.rang}` : 'LV';
    const type = (o.typeLangueRequis ?? [])
      .map((t) => (t === 'LVE' ? 'étrangère' : t === 'LVR' ? 'régionale' : t))
      .join('/');
    return `${o.label} · ${rang}${type ? ` ${type}` : ''} (${o.code})`;
  }
  return `${o.label} (${o.code})`;
}

interface Props {
  open: boolean;
  schoolId: number;
  schoolName?: string;
  lotCode: string;
  lotLabel: string;
  /**
   * Statut Éducation Nationale de l'école. À `false`, le drawer passe en mode
   * « label libre » : pas de nomenclature imposée — par défaut le libellé V1
   * est conservé tel quel en V2 (passe-plat), et chaque ligne peut être
   * renommée librement. Pas de colonne MEF/BCN ni de pré-mapping IA.
   */
  isEducationNationale?: boolean;
  onClose: () => void;
}

interface EditState {
  targetCode: string | null;
  targetLabel: string | null;
  /** Code EN (MEF pour niveau, BCN pour matière) surchargé pour cette école. */
  enCode?: string | null;
  /** Libellé du code EN suggéré (affichage MEF — non persisté). */
  enLabel?: string | null;
  /**
   * Cycle du niveau (code normalisé) — undefined = non touché (la valeur
   * affichée retombe sur `row.cycleCode`), null = effacé explicitement.
   */
  cycleCode?: string | null;
  dirty: boolean;
  /** Provenance : 'ai' si pré-rempli par Gemini (icône ✨), absent sinon. */
  source?: 'ai';
  confidence?: number | null;
}

/**
 * Drawer latéral droit pour mapper les libellés V1 d'un lot vers la
 * nomenclature normalisée V2 (`data.ecole-futee.com/{cycles|niveaux}`).
 *
 * Les IDs V1 (`cycleID`/`sectionID`) n'ont pas le même sens d'une base à
 * l'autre, mais les libellés ("CP", "GS"…) sont communs : un catalogue de
 * **défauts globaux indexés par libellé** (saisi dans la Configuration) est
 * appliqué automatiquement. Chaque ligne reconnue est pré-mappée (« Défaut
 * global », éditable) ; seules les lignes inconnues restent « À mapper » et
 * peuvent être surchargées spécifiquement pour cette école.
 */
export function LotMappingDialog({
  open,
  schoolId,
  schoolName,
  lotCode,
  lotLabel,
  isEducationNationale = true,
  onClose,
}: Props) {
  const config = LOT_MAPPING_REGISTRY[lotCode];
  // Mode label libre (école non-EN) : cible = texte libre, défaut = libellé V1.
  const freeLabelMode = !isEducationNationale;
  // Matières d'une école EN : le mapping se fait UNIQUEMENT par association
  // matière × niveau (code BCN défini par MEF) — l'ancien tableau matière→V2
  // n'a plus lieu d'être (le nom V2 se déduit de la nomenclature BCN).
  const subjectsEnMode = lotCode === 'school.subjects' && !freeLabelMode;
  const enCodeKind = freeLabelMode ? undefined : config?.enCodeKind;
  // Niveaux (EN) : colonne Cycle — pré-remplie du cycle V1 mappé, éditable
  // pour compléter les niveaux V1 sans cycle (override `niveau_cycle`).
  const cycleColumn = lotCode === 'school.levels' && !freeLabelMode;
  const enSearch = enCodeKind === 'mef'
    ? migrationV1V2Api.searchEnMefs
    : enCodeKind === 'matiere'
      ? migrationV1V2Api.searchEnMatieres
      : null;
  const enColLabel = enCodeKind === 'mef' ? 'Code MEF' : 'Code BCN';
  const [data, setData] = useState<ReferentialOverridesResponse | null>(null);
  const [v2Items, setV2Items] = useState<V2DataItem[]>([]);
  const [cycleItems, setCycleItems] = useState<V2DataItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [edits, setEdits] = useState<Record<string, EditState>>({});
  const [savingAll, setSavingAll] = useState(false);
  // Pré-mapping IA (Gemini) : null = inconnu, true/false = clé configurée ou non.
  const [aiEnabled, setAiEnabled] = useState<boolean | null>(null);
  const [aiRunning, setAiRunning] = useState(false);
  const [aiCount, setAiCount] = useState(0);

  const load = async () => {
    // Garde : ce dialog ne sait gérer que les lots à catalogue V2 normalisé.
    // Les lots `custom_field` (sans v2Resource) sont gérés ailleurs par
    // `CustomFieldsMappingDialog` via le dispatcher de `ProjectDetailPage`.
    if (!config || !config.v2Resource) return;
    // Matières EN : aucune donnée générique à charger — la section association
    // matière × niveau (MatiereNiveauBcnSection) se charge elle-même.
    if (subjectsEnMode) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      // Mode label libre : pas de nomenclature à charger ni de pré-mapping IA
      // (le défaut est le libellé V1, pas un code BCN).
      const [overrides, items, cycles] = await Promise.all([
        migrationV1V2Api.getReferentialOverrides(schoolId, config.type, true),
        freeLabelMode
          ? Promise.resolve([] as V2DataItem[])
          : migrationV1V2Api.getV2DataResource(config.v2Resource, schoolId),
        cycleColumn
          ? migrationV1V2Api.getV2DataResource('cycles', schoolId)
          : Promise.resolve([] as V2DataItem[]),
      ]);
      setData(overrides);
      setV2Items(items);
      setCycleItems(cycles);
      setEdits({});
      // Pré-mapping IA aussi en mode label libre (non-EN) : regroupement des
      // sections déclinées au grain classe derrière un libellé de niveau
      // canonique (le backend renvoie [] pour les types non regroupables).
      void runAiSuggest(overrides);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  };

  /**
   * Pré-mapping IA : suggère un targetCode pour les libellés V1 non encore
   * mappés (drawer). Pré-remplit en « modifié » (✨) ; l'opérateur enregistre.
   *
   * Niveaux (EN) : suggère aussi le code MEF des lignes mappées qui n'en ont
   * pas (ni override ni hérité du global) — déterministe quand le niveau n'a
   * qu'un MEF (primaire/collège, sans IA), Gemini pour les cas ambigus
   * (séries de lycée : « 1ère STMG » vs « première générale »).
   */
  const runAiSuggest = async (overrides: ReferentialOverridesResponse) => {
    setAiCount(0);
    if (!config?.v2Resource) return;
    // Mode label libre : « non mappé » = pas de surcharge école (le défaut
    // global du catalogue BCN est ignoré pour les non-EN). Sinon : pas de code.
    const unmapped = overrides.mappings.filter((r) =>
      freeLabelMode ? !r.hasOverride : !r.targetCode
    );
    const labels = unmapped
      .map((r) => r.v1LiveLabel ?? r.sourceLabel ?? '')
      .filter((l) => l !== '');
    // Lignes déjà mappées sur un niveau mais sans MEF → cibles de suggestion.
    const mefTargets =
      enCodeKind === 'mef' && !freeLabelMode
        ? overrides.mappings
            .filter((r) => !!r.targetCode && !r.enCode)
            .map((r) => ({
              sourceLabel: r.v1LiveLabel ?? r.sourceLabel ?? '',
              targetCode: r.targetCode as string,
            }))
            .filter((t) => t.sourceLabel !== '')
        : [];
    if (labels.length === 0 && mefTargets.length === 0) return;

    // Niveaux V1 associés à chaque libellé (matières) : contexte de
    // dédoublonnage pour l'IA — les déclinaisons par niveau (« Français CP »,
    // « Français CE1 ») doivent converger vers la même matière V2.
    const sourceLevels: Record<string, string> = {};
    for (const r of unmapped) {
      const label = r.v1LiveLabel ?? r.sourceLabel ?? '';
      if (label !== '' && r.v1Levels) sourceLevels[label] = r.v1Levels;
    }

    setAiRunning(true);
    try {
      const resp = await migrationV1V2Api.aiSuggestReferential(
        schoolId,
        config.type,
        labels,
        Object.keys(sourceLevels).length > 0 ? sourceLevels : undefined,
        mefTargets.length > 0 ? mefTargets : undefined
      );
      setAiEnabled(resp.enabled);
      const byLabel = new Map(resp.suggestions.map((s) => [s.sourceLabel, s]));
      const mefByLabel = new Map(
        (resp.mefSuggestions ?? []).map((s) => [s.sourceLabel, s])
      );
      if (byLabel.size === 0 && mefByLabel.size === 0) return;
      const next: Record<string, EditState> = {};
      let count = 0;
      for (const r of unmapped) {
        const label = r.v1LiveLabel ?? r.sourceLabel ?? '';
        const s = byLabel.get(label);
        if (s?.targetCode) {
          const mef = mefByLabel.get(label);
          next[r.sourceId] = {
            targetCode: s.targetCode,
            // Mode label libre : le libellé canonique sert de code ET de label
            // (le champ texte du drawer lit `targetLabel`).
            targetLabel: s.targetLabel ?? s.targetCode,
            // enCode existant préservé ; niveaux : MEF suggéré dans la foulée ;
            // matières : nomenclature = table BCN, la suggestion vaut aussi
            // code BCN (sinon `codeMatiere` ne serait jamais poussé en V2).
            // Neutralisé en label libre (non-EN).
            enCode: freeLabelMode
              ? null
              : r.enCode ??
                mef?.enCode ??
                (config.enCodeKind === 'matiere' ? s.targetCode : null),
            enLabel: freeLabelMode ? null : mef?.enLabel ?? null,
            dirty: true,
            source: 'ai',
            confidence: s.confidence,
          };
          count++;
        }
      }
      // Lignes déjà mappées (défaut global ou override) : seule la colonne
      // MEF est pré-remplie, le niveau choisi n'est pas touché.
      for (const r of overrides.mappings) {
        if (!r.targetCode || r.enCode || next[r.sourceId]) continue;
        const mef = mefByLabel.get(r.v1LiveLabel ?? r.sourceLabel ?? '');
        if (!mef?.enCode) continue;
        next[r.sourceId] = {
          targetCode: r.targetCode,
          targetLabel: r.targetLabel,
          enCode: mef.enCode,
          enLabel: mef.enLabel ?? null,
          dirty: true,
          source: 'ai',
          confidence: mef.confidence,
        };
        count++;
      }
      if (count > 0) setEdits((prev) => ({ ...next, ...prev }));
      setAiCount(count);
    } catch {
      /* best-effort : pas de blocage du drawer */
    } finally {
      setAiRunning(false);
    }
  };

  useEffect(() => {
    if (open) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, schoolId, lotCode]);

  const rows = useMemo(() => data?.mappings ?? [], [data]);

  /**
   * Normalise un item de nomenclature en `{code, label}`. La forme exacte
   * varie d'une ressource à l'autre côté `data.ecole-futee.com` : `matieres`
   * (BCN) expose `matiereEnseignee` + `libelle*MatiereEnseigne`, là où
   * `cycles`/`niveaux` exposent `code` + `libelle*` (même logique que le
   * `ReferentialMappingAiPremapper` backend).
   */
  const v2Options = useMemo(
    () =>
      v2Items.map((it) => {
        const code = String(it.code ?? it.matiereEnseignee ?? it.id ?? '').trim();
        const label = String(
          it.libelleLong ?? it.libelleLongMatiereEnseigne
            ?? it.libelleCourt ?? it.libelleCourtMatiereEnseigne
            ?? it.libelle ?? it.libelleEdition ?? it.label ?? it.name ?? code
        ).trim();
        return { code, label };
      }).filter((o) => o.code !== ''),
    [v2Items]
  );

  const v2LabelForCode = (code: string | null): string | null => {
    if (!code) return null;
    return v2Options.find((o) => o.code === code)?.label ?? null;
  };

  /** Nomenclature des cycles (colonne Cycle des niveaux), même normalisation. */
  const cycleOptions = useMemo(
    () =>
      cycleItems.map((it) => {
        const code = String(it.code ?? it.id ?? '').trim();
        const label = String(
          it.libelleLong ?? it.libelleCourt ?? it.libelle ?? it.libelleEdition ?? it.label ?? it.name ?? code
        ).trim();
        return { code, label };
      }).filter((o) => o.code !== ''),
    [cycleItems]
  );

  const cycleLabelForCode = (code: string | null): string | null => {
    if (!code) return null;
    return cycleOptions.find((o) => o.code === code)?.label ?? null;
  };

  const editFor = (row: ReferentialOverrideRow): EditState => {
    // Mode label libre : seule la surcharge école compte — le défaut global
    // (catalogue de normalisation BCN) ne s'applique pas aux écoles non-EN
    // (même règle que les loaders backend).
    return (
      edits[row.sourceId] ?? {
        targetCode: freeLabelMode ? (row.hasOverride ? row.targetCode : null) : row.targetCode,
        targetLabel: freeLabelMode ? (row.hasOverride ? row.targetLabel : null) : row.targetLabel,
        enCode: freeLabelMode ? null : row.enCode,
        dirty: false,
      }
    );
  };

  const setEdit = (sourceId: string, patch: Partial<EditState>) => {
    setEdits((prev) => {
      // Seed depuis la ligne courante pour ne pas écraser les autres champs
      // (ex. modifier le code EN seul sans perdre le targetCode existant).
      const row = rows.find((r) => r.sourceId === sourceId);
      const base = prev[sourceId] ?? {
        targetCode: freeLabelMode ? (row?.hasOverride ? row.targetCode : null) : row?.targetCode ?? null,
        targetLabel: freeLabelMode ? (row?.hasOverride ? row.targetLabel : null) : row?.targetLabel ?? null,
        enCode: freeLabelMode ? null : row?.enCode ?? null,
        dirty: false,
      };
      return {
        ...prev,
        [sourceId]: {
          ...base,
          ...patch,
          dirty: true,
          // Choix manuel → on retire l'origine IA (l'✨ disparaît).
          source: undefined,
          confidence: undefined,
        },
      };
    });
  };

  /**
   * Regroupement visuel par cible V2 : plusieurs libellés V1 pointent la même
   * valeur normalisée. Pour les matières c'est un vrai dédoublonnage (UNE
   * matière V2) ; pour les niveaux c'est purement informatif — la migration
   * ne fusionne PAS (1 classe V1 = 1 niveau V2, le libellé V1 est conservé).
   * Les lignes non mappées restent en tête, puis les cibles triées par
   * libellé ; un en-tête de groupe n'apparaît que si ≥ 2 libellés convergent.
   */
  const rowGroups = useMemo(() => {
    const byCode = new Map<string, ReferentialOverrideRow[]>();
    const unmapped: ReferentialOverrideRow[] = [];
    // Mode label libre : liste plate — le passe-plat n'est pas un état
    // « à mapper », et le regroupement par code n'a pas de sens.
    if (freeLabelMode) {
      return { unmapped: rows, mapped: [] as { code: string; label: string; rows: ReferentialOverrideRow[] }[] };
    }
    for (const r of rows) {
      const code = (edits[r.sourceId] ?? r).targetCode;
      if (!code) {
        unmapped.push(r);
        continue;
      }
      const list = byCode.get(code);
      if (list) list.push(r);
      else byCode.set(code, [r]);
    }
    const labelOf = (code: string) =>
      v2Options.find((o) => o.code === code)?.label ?? code;
    const mapped = [...byCode.entries()]
      .map(([code, groupRows]) => ({ code, label: labelOf(code), rows: groupRows }))
      .sort((a, b) => a.label.localeCompare(b.label, 'fr'));
    return { unmapped, mapped };
  }, [rows, edits, v2Options, freeLabelMode]);

  const colCount = 5 + (enCodeKind ? 1 : 0) + (cycleColumn ? 1 : 0);

  const dirtyRows = useMemo(
    () => rows.filter((r) => edits[r.sourceId]?.dirty),
    [rows, edits]
  );
  const dirtyCount = dirtyRows.length;

  const mappedCount = useMemo(
    () =>
      rows.filter((r) => {
        const e = edits[r.sourceId];
        if (e) return !!e.targetCode;
        // Mode label libre : seule la surcharge école compte (le défaut
        // global BCN ne s'applique pas aux écoles non-EN).
        return freeLabelMode ? r.hasOverride && !!r.targetCode : !!r.targetCode;
      }).length,
    [rows, edits, freeLabelMode]
  );

  /** Enregistre en une passe tous les libellés modifiés du lot. */
  const saveAll = async () => {
    if (dirtyCount === 0) return;
    setSavingAll(true);
    setError(null);
    try {
      for (const row of dirtyRows) {
        const e = edits[row.sourceId];
        if (!e) continue;
        // `enCodeKind` effectif : neutralisé en mode label libre (non-EN) —
        // on efface au passage un éventuel enCode résiduel de l'override.
        const enCode = enCodeKind ? (e.enCode ?? null) : null;
        // Un override n'existe que s'il porte au moins un targetCode OU un code EN
        // (surcharge du seul MEF/BCN possible, le niveau/nom restant global).
        if (!e.targetCode && !enCode) {
          await migrationV1V2Api.deleteReferentialOverride(schoolId, config.type, row.sourceId);
        } else {
          await migrationV1V2Api.upsertReferentialOverride(schoolId, {
            type: config.type,
            sourceId: row.sourceId,
            sourceLabel: row.v1LiveLabel ?? row.sourceLabel ?? null,
            targetCode: e.targetCode,
            targetLabel: e.targetCode ? (e.targetLabel ?? v2LabelForCode(e.targetCode) ?? null) : null,
            enCode,
          });
        }

        // Cycle du niveau (override `niveau_cycle`) : persisté seulement si la
        // valeur a réellement changé — un cycle hérité du V1 non touché reste
        // dérivé du live (pas figé en override).
        if (cycleColumn && e.cycleCode !== undefined && e.cycleCode !== (row.cycleCode ?? null)) {
          if (e.cycleCode === null) {
            // Retour au défaut (cycle V1 mappé, ou aucun) : on retire l'override.
            await migrationV1V2Api.deleteReferentialOverride(schoolId, 'niveau_cycle', row.sourceId);
          } else {
            await migrationV1V2Api.upsertReferentialOverride(schoolId, {
              type: 'niveau_cycle',
              sourceId: row.sourceId,
              sourceLabel: row.v1LiveLabel ?? row.sourceLabel ?? null,
              targetCode: e.cycleCode,
              targetLabel: cycleLabelForCode(e.cycleCode),
            });
          }
        }
      }
      await load();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSavingAll(false);
    }
  };

  const clearMapping = async (row: ReferentialOverrideRow) => {
    if (!row.hasOverride) return;
    if (!confirm(`Supprimer le mapping pour "${row.v1LiveLabel ?? row.sourceLabel}" ?`)) return;
    try {
      await migrationV1V2Api.deleteReferentialOverride(schoolId, config.type, row.sourceId);
      await load();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : String(err));
    }
  };

  const saveButton = (
    <Button onClick={saveAll} disabled={dirtyCount === 0 || savingAll}>
      {savingAll ? (
        <Loader2 className="h-4 w-4 animate-spin" />
      ) : (
        <Save className="h-4 w-4" />
      )}
      Enregistrer{dirtyCount > 0 ? ` (${dirtyCount})` : ''}
    </Button>
  );

  return (
    <Sheet open={open} onOpenChange={(v) => !v && onClose()}>
      <SheetContent side="right" className="sm:w-[min(96vw,1400px)] sm:max-w-[1400px]">
        <SheetHeader>
          <div className="flex items-start justify-between gap-4">
            <div className="flex flex-col space-y-1">
              <SheetTitle>
                Mapping {lotLabel}{schoolName ? ` — ${schoolName}` : ''}
              </SheetTitle>
              <SheetDescription>
                {subjectsEnMode ? (
                  <>
                    Chaque association matière × niveau reçoit son code BCN (la
                    nomenclature BCN est définie par MEF). Le nom V2 de la matière se
                    déduit du code choisi — pas de mapping « Matière V2 » séparé.
                  </>
                ) : freeLabelMode ? (
                  <>
                    École hors Éducation Nationale : chaque libellé V1 est conservé
                    tel quel en V2 par défaut. Saisissez un libellé V2 uniquement
                    pour renommer une ligne — laissez vide pour garder le libellé V1.
                  </>
                ) : (
                  <>
                    Associer chaque libellé V1 à une valeur normalisée de la nomenclature{' '}
                    <code>data.ecole-futee.com/{config?.v2Resource}</code>. Les libellés
                    reconnus sont pré-remplis depuis les défauts globaux ; une valeur
                    définie ici surcharge le défaut pour cette école.
                  </>
                )}
              </SheetDescription>
            </div>
            {config && data && !subjectsEnMode && <div className="shrink-0">{saveButton}</div>}
          </div>
        </SheetHeader>

        <SheetBody>
          {!config && (
            <div className="rounded border border-red-200 bg-red-50 px-4 py-3 text-red-800">
              Lot inconnu : <code>{lotCode}</code>
            </div>
          )}

          {config && subjectsEnMode && (
            <div className="space-y-4" style={{ textTransform: 'none' }}>
              <MatiereNiveauBcnSection schoolId={schoolId} />
            </div>
          )}

          {config && !subjectsEnMode && loading && !data && (
            <div className="flex items-center justify-center py-8 text-gray-500" style={{ textTransform: 'none' }}>
              <Loader2 className="h-5 w-5 animate-spin mr-2" />
              Chargement…
            </div>
          )}

          {config && !subjectsEnMode && data && (
            <div className="space-y-4" style={{ textTransform: 'none' }}>
              <div
                className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3"
                style={{ color: '#78350f' }}
              >
                <div className="font-semibold mb-1 flex items-center gap-2 text-sm" style={{ color: '#78350f' }}>
                  <AlertTriangle className="h-4 w-4" />
                  Mapping {lotLabel.toLowerCase()} V1 → V2 (école {schoolName ?? schoolId})
                </div>
                <p
                  className="text-sm leading-relaxed"
                  style={{
                    color: '#92400e',
                    textTransform: 'none',
                    fontSize: '0.8125rem',
                    fontWeight: 400,
                    letterSpacing: 'normal',
                    display: 'block',
                  }}
                >
                  {freeLabelMode ? (
                    <>
                      École hors Éducation Nationale : aucun mapping n'est requis pour
                      migrer — chaque libellé V1 part tel quel en V2. Renseignez le
                      champ « {config.v2Label} » d'une ligne uniquement pour la renommer.
                    </>
                  ) : (
                    <>
                      Chaque libellé V1 doit être associé à une valeur normalisée pour que
                      la migration puisse continuer. Les libellés présents dans le catalogue
                      global sont déjà mappés (« Défaut global ») ; complétez uniquement les
                      lignes « À mapper » propres à cette école.
                    </>
                  )}
                  {!data.v1Live.available && (
                    <span className="block mt-1 text-amber-700">
                      ⚠️ Lecture live de la table V1 indisponible
                      {data.v1Live.error ? ` (${data.v1Live.error})` : ''}.
                    </span>
                  )}
                  {!freeLabelMode && v2Options.length === 0 && (
                    <span className="block mt-1 text-amber-700">
                      ⚠️ Nomenclature V2 vide ou non chargée.
                    </span>
                  )}
                </p>
              </div>

              {/* Le pré-remplissage MEF déterministe fonctionne même IA coupée
                  (aiEnabled false) : le bandeau s'affiche dès qu'il y a des
                  suggestions appliquées. */}
              {(aiRunning || aiCount > 0) && (
                <div
                  className="flex items-center gap-2 rounded-lg border border-violet-200 bg-violet-50 px-4 py-2 text-sm"
                  style={{ color: '#5b21b6', textTransform: 'none' }}
                >
                  {aiRunning ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Pré-mapping IA en cours…
                    </>
                  ) : (
                    <>
                      <Sparkles className="h-4 w-4 text-violet-500" />
                      {aiCount} suggestion{aiCount > 1 ? 's' : ''} IA pré-remplie{aiCount > 1 ? 's' : ''} (✨)
                      — vérifiez puis Enregistrer.
                    </>
                  )}
                </div>
              )}

              {error && (
                <div
                  className="rounded-lg border border-red-200 bg-red-50 px-4 py-2 text-sm"
                  style={{ color: '#991b1b' }}
                >
                  {error}
                </div>
              )}

              <div className="rounded-lg border border-gray-200 bg-white overflow-hidden">
                <table className="w-full text-sm" style={{ tableLayout: 'fixed' }}>
                  <colgroup>
                    <col style={{ width: '60px' }} />
                    <col style={{ width: cycleColumn ? '18%' : enCodeKind ? '24%' : '32%' }} />
                    <col style={{ width: cycleColumn ? '20%' : enCodeKind ? '26%' : '38%' }} />
                    {enCodeKind && <col style={{ width: cycleColumn ? '20%' : '22%' }} />}
                    {cycleColumn && <col style={{ width: '16%' }} />}
                    <col style={{ width: cycleColumn ? '10%' : '14%' }} />
                    <col style={{ width: cycleColumn ? '12%' : '14%' }} />
                  </colgroup>
                  <thead>
                    <tr style={{ background: '#f8fafc' }}>
                      <th
                        className="px-3 py-2.5 text-left font-medium text-gray-700 border-b border-gray-200"
                        style={{ textTransform: 'none', fontSize: '0.75rem', letterSpacing: 'normal' }}
                      >
                        ID V1
                      </th>
                      <th
                        className="px-3 py-2.5 text-left font-medium text-gray-700 border-b border-gray-200"
                        style={{ textTransform: 'none', fontSize: '0.75rem', letterSpacing: 'normal' }}
                      >
                        Libellé V1
                      </th>
                      <th
                        className="px-3 py-2.5 text-left font-medium text-gray-700 border-b border-gray-200"
                        style={{ textTransform: 'none', fontSize: '0.75rem', letterSpacing: 'normal' }}
                      >
                        {config.v2Label}
                      </th>
                      {enCodeKind && (
                        <th
                          className="px-3 py-2.5 text-left font-medium text-gray-700 border-b border-gray-200"
                          style={{ textTransform: 'none', fontSize: '0.75rem', letterSpacing: 'normal' }}
                        >
                          {enColLabel} (Éd. Nat.)
                        </th>
                      )}
                      {cycleColumn && (
                        <th
                          className="px-3 py-2.5 text-left font-medium text-gray-700 border-b border-gray-200"
                          style={{ textTransform: 'none', fontSize: '0.75rem', letterSpacing: 'normal' }}
                        >
                          Cycle
                        </th>
                      )}
                      <th
                        className="px-3 py-2.5 text-left font-medium text-gray-700 border-b border-gray-200"
                        style={{ textTransform: 'none', fontSize: '0.75rem', letterSpacing: 'normal' }}
                      >
                        Statut
                      </th>
                      <th
                        className="px-3 py-2.5 text-right font-medium text-gray-700 border-b border-gray-200"
                        style={{ textTransform: 'none', fontSize: '0.75rem', letterSpacing: 'normal' }}
                      >
                        Action
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {rowGroups.unmapped.length > 0 && rowGroups.mapped.length > 0 && (
                      <tr className="border-b border-gray-100">
                        <td
                          colSpan={colCount}
                          className="px-3 py-1.5"
                          style={{ background: '#fef2f2', textTransform: 'none' }}
                        >
                          <span className="text-xs font-semibold" style={{ color: '#991b1b' }}>
                            À mapper ({rowGroups.unmapped.length})
                          </span>
                        </td>
                      </tr>
                    )}
                    {rowGroups.unmapped.map((row) => renderRow(row))}
                    {rowGroups.mapped.map((g) => (
                      <Fragment key={g.code}>
                        {g.rows.length > 1 && (
                          <tr className="border-b border-gray-100">
                            <td
                              colSpan={colCount}
                              className="px-3 py-1.5"
                              style={{ background: '#eef2ff', textTransform: 'none' }}
                            >
                              <span
                                className="inline-flex items-center gap-1.5 text-xs font-semibold"
                                style={{ color: '#3730a3' }}
                              >
                                <Layers className="h-3.5 w-3.5" />
                                {g.label}
                                <span className="font-mono font-normal" style={{ color: '#818cf8' }}>
                                  {g.code}
                                </span>
                                <span className="font-normal" style={{ color: '#6366f1' }}>
                                  — {g.rows.length} libellés V1{' '}
                                  {lotCode === 'school.levels'
                                    ? 'sur cette valeur (chacun reste un niveau V2 distinct)'
                                    : 'regroupés'}
                                </span>
                              </span>
                            </td>
                          </tr>
                        )}
                        {g.rows.map((row) => renderRow(row))}
                      </Fragment>
                    ))}
                    {rows.length === 0 && (
                      <tr>
                        <td colSpan={colCount} className="px-3 py-8 text-center text-gray-500" style={{ textTransform: 'none' }}>
                          Aucun libellé V1 détecté pour ce type. La table V1
                          correspondante est peut-être vide.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </SheetBody>

        {config && data && (
          <SheetFooter>
            <div className="flex w-full items-center justify-between gap-4">
              <span className="text-sm text-gray-500" style={{ textTransform: 'none' }}>
                {freeLabelMode ? (
                  <>
                    {mappedCount} renommé{mappedCount > 1 ? 's' : ''} ·{' '}
                    {rows.length - mappedCount} conservé{rows.length - mappedCount > 1 ? 's' : ''} tel quel
                  </>
                ) : (
                  <>
                    {mappedCount} / {rows.length} mappé{rows.length > 1 ? 's' : ''}
                    {rowGroups.mapped.length > 0 && mappedCount > rowGroups.mapped.length
                      ? ` · regroupés en ${rowGroups.mapped.length} cible${rowGroups.mapped.length > 1 ? 's' : ''} V2 distincte${rowGroups.mapped.length > 1 ? 's' : ''}`
                      : ''}
                  </>
                )}
                {dirtyCount > 0 ? ` · ${dirtyCount} modification${dirtyCount > 1 ? 's' : ''} non enregistrée${dirtyCount > 1 ? 's' : ''}` : ''}
              </span>
              {saveButton}
            </div>
          </SheetFooter>
        )}
      </SheetContent>
    </Sheet>
  );

  function renderRow(row: ReferentialOverrideRow) {
    const e = editFor(row);
                      const effectiveCode = e.targetCode;
                      const isMapped = !!effectiveCode;
                      // Trois états : surcharge école (override ou édition en
                      // cours), défaut global (valeur héritée du catalogue,
                      // non surchargée), ou non mappé.
                      const isGlobalDefault =
                        !freeLabelMode && isMapped && !row.hasOverride && !e.dirty && effectiveCode === row.globalTargetCode;
                      const v1Label = row.v1LiveLabel ?? row.sourceLabel ?? '—';
                      // Nom V2 : nomenclature chargée, sinon label porté par
                      // l'édition (suggestion IA / override enregistré).
                      const v2Label = v2LabelForCode(effectiveCode) ?? e.targetLabel ?? null;

                      return (
                        <tr
                          key={row.sourceId}
                          className="border-b border-gray-100 last:border-b-0"
                        >
                          <td
                            className="px-3 py-2 font-mono text-xs text-gray-500 align-middle"
                            style={{ textTransform: 'none' }}
                          >
                            {row.sourceId}
                          </td>
                          <td className="px-3 py-2 align-middle">
                            <div className="text-gray-900 font-medium flex items-center gap-1" style={{ textTransform: 'none' }}>
                              {v1Label}
                              {e.source === 'ai' && (
                                <span
                                  className="inline-flex"
                                  title={`Pré-rempli par l'IA${
                                    e.confidence != null ? ` · confiance ${Math.round(e.confidence * 100)}%` : ''
                                  }`}
                                >
                                  <Sparkles className="h-3.5 w-3.5 text-violet-500" />
                                </span>
                              )}
                            </div>
                            {row.v1Levels && (
                              <div
                                className="text-xs text-gray-400 mt-0.5"
                                style={{ textTransform: 'none' }}
                                title="Niveaux associés dans la V1"
                              >
                                {row.v1Levels}
                              </div>
                            )}
                          </td>
                          <td className="px-3 py-2 align-middle">
                            {freeLabelMode ? (
                              <Input
                                className="h-9 text-sm"
                                style={{ textTransform: 'none' }}
                                value={e.targetLabel ?? ''}
                                placeholder={`${v1Label} (conservé tel quel)`}
                                onChange={(ev) => {
                                  const label = ev.target.value;
                                  const trimmed = label.trim();
                                  // Le label libre sert de code ET de libellé
                                  // (le resolver backend exige un targetCode
                                  // non nul pour appliquer la surcharge).
                                  setEdit(row.sourceId, {
                                    targetCode: trimmed === '' ? null : trimmed,
                                    targetLabel: trimmed === '' ? null : label,
                                  });
                                }}
                              />
                            ) : (
                            <Select
                              value={effectiveCode ?? NONE_VALUE}
                              onValueChange={(v) => {
                                const code = v === NONE_VALUE ? null : v;
                                setEdit(row.sourceId, {
                                  targetCode: code,
                                  targetLabel: code ? v2LabelForCode(code) : null,
                                  // Matières : la nomenclature normalisée EST la
                                  // table BCN — le code choisi vaut code BCN
                                  // (reste modifiable dans sa colonne).
                                  ...(enCodeKind === 'matiere' ? { enCode: code } : {}),
                                });
                              }}
                            >
                              <SelectTrigger className="h-9 text-sm" style={{ textTransform: 'none' }}>
                                <SelectValue>
                                  {effectiveCode ? (
                                    <span style={{ textTransform: 'none' }}>
                                      {v2Label ?? effectiveCode}
                                      {v2Label && (
                                        <span className="text-gray-400 text-xs font-mono ml-1.5">
                                          {effectiveCode}
                                        </span>
                                      )}
                                    </span>
                                  ) : (
                                    <span className="text-gray-400">(non mappé)</span>
                                  )}
                                </SelectValue>
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value={NONE_VALUE}>
                                  <span style={{ textTransform: 'none' }}>(non mappé)</span>
                                </SelectItem>
                                {v2Options.map((o) => (
                                  <SelectItem key={o.code} value={o.code}>
                                    <span style={{ textTransform: 'none' }}>
                                      {o.label}{' '}
                                      <span className="text-gray-400 text-xs font-mono">
                                        {o.code}
                                      </span>
                                    </span>
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            )}
                          </td>
                          {enCodeKind && enSearch && (
                            <td className="px-3 py-2 align-middle">
                              <RemoteSearchSelect
                                value={e.enCode ?? ''}
                                // Matières : le code BCN vit dans la même
                                // nomenclature que la colonne V2 → libellé
                                // résolu localement. MEF : code brut.
                                selectedLabel={
                                  e.enCode
                                    ? (() => {
                                        const label =
                                          v2LabelForCode(e.enCode ?? null) ?? e.enLabel ?? null;
                                        return label ? `${label} · ${e.enCode}` : e.enCode;
                                      })()
                                    : null
                                }
                                search={(q) =>
                                  enSearch(q).then((opts) =>
                                    opts.map((o) => ({ code: o.code, label: enOptionLabel(o) }))
                                  )
                                }
                                onSelect={(code) => setEdit(row.sourceId, { enCode: code || null })}
                                placeholder={enCodeKind === 'mef' ? 'MEF…' : 'Code BCN…'}
                              />
                            </td>
                          )}
                          {cycleColumn && (
                            <td className="px-3 py-2 align-middle">
                              <Select
                                value={(e.cycleCode !== undefined ? e.cycleCode : row.cycleCode ?? null) ?? NONE_VALUE}
                                onValueChange={(v) =>
                                  setEdit(row.sourceId, { cycleCode: v === NONE_VALUE ? null : v })
                                }
                              >
                                <SelectTrigger className="h-9 text-sm" style={{ textTransform: 'none' }}>
                                  <SelectValue>
                                    {(() => {
                                      const code = e.cycleCode !== undefined ? e.cycleCode : row.cycleCode ?? null;
                                      if (!code) return <span className="text-gray-400">(aucun)</span>;
                                      return (
                                        <span style={{ textTransform: 'none' }}>
                                          {cycleLabelForCode(code) ?? row.cycleLabel ?? code}
                                        </span>
                                      );
                                    })()}
                                  </SelectValue>
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value={NONE_VALUE}>
                                    <span style={{ textTransform: 'none' }}>(aucun)</span>
                                  </SelectItem>
                                  {cycleOptions.map((o) => (
                                    <SelectItem key={o.code} value={o.code}>
                                      <span style={{ textTransform: 'none' }}>
                                        {o.label}{' '}
                                        <span className="text-gray-400 text-xs font-mono">{o.code}</span>
                                      </span>
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                              <div
                                className="text-xs text-gray-400 mt-0.5"
                                style={{ textTransform: 'none' }}
                                title="Cycle associé dans la V1"
                              >
                                {row.v1CycleLabel ? `V1 : ${row.v1CycleLabel}` : 'aucun cycle V1'}
                              </div>
                            </td>
                          )}
                          <td className="px-3 py-2 align-middle">
                            {!isMapped ? (
                              freeLabelMode ? (
                                <Badge
                                  className="bg-gray-100 border-gray-200"
                                  style={{ color: '#374151', textTransform: 'none', fontSize: '0.75rem' }}
                                >
                                  Libellé V1 conservé
                                </Badge>
                              ) : (
                                <Badge
                                  className="bg-red-100 border-red-200"
                                  style={{ color: '#991b1b', textTransform: 'none', fontSize: '0.75rem' }}
                                >
                                  À mapper
                                </Badge>
                              )
                            ) : isGlobalDefault ? (
                              <Badge
                                className="bg-indigo-100 border-indigo-200"
                                style={{ color: '#3730a3', textTransform: 'none', fontSize: '0.75rem' }}
                              >
                                Défaut global
                              </Badge>
                            ) : (
                              <Badge
                                className="bg-emerald-100 border-emerald-200"
                                style={{ color: '#065f46', textTransform: 'none', fontSize: '0.75rem' }}
                              >
                                {freeLabelMode ? 'Renommé' : 'Mappé'}
                              </Badge>
                            )}
                          </td>
                          <td className="px-3 py-2 text-right align-middle">
                            <div className="inline-flex items-center gap-1">
                              {e.dirty && (
                                <Badge
                                  className="bg-amber-100 border-amber-200"
                                  style={{ color: '#92400e', textTransform: 'none', fontSize: '0.7rem' }}
                                >
                                  Modifié
                                </Badge>
                              )}
                              {row.hasOverride && (
                                <Button size="sm" variant="ghost" onClick={() => clearMapping(row)}>
                                  <RotateCcw className="h-3.5 w-3.5" />
                                  Effacer
                                </Button>
                              )}
                            </div>
                          </td>
                        </tr>
    );
  }
}
